import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { adminApi } from "../../api/admin";
import { formatCredits, formatMoney } from "../../shared/format";
import { Card, Spinner } from "../../ui";

function Bars({
  title,
  points,
}: {
  title: string;
  points: { date: string; value: number }[];
}) {
  const max = Math.max(1, ...points.map((p) => p.value));
  return (
    <Card>
      <h2 className="text-sm font-medium">{title}</h2>
      {points.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">No data in range.</p>
      ) : (
        <div
          className="mt-2 flex h-24 items-end gap-0.5"
          role="img"
          aria-label={`${title} chart`}
        >
          {points.map((p) => (
            <div
              key={p.date}
              title={`${p.date}: ${p.value}`}
              className="flex-1 rounded-t bg-brand-tint/500"
              style={{ height: `${Math.max(4, (p.value / max) * 100)}%` }}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <p className="text-xs uppercase text-slate-500">{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
    </Card>
  );
}

/** One product's overview: its own users, money and delivery health (docs/07 §3, docs/14 §5). */
export function OverviewPage() {
  const { product } = useOutletContext<{ product: string | null }>();
  const [days, setDays] = useState(30);
  const metrics = useQuery({
    queryKey: ["admin", "metrics", product, days],
    queryFn: () =>
      adminApi.metrics({
        product: product ?? undefined,
        from: new Date(Date.now() - days * 86_400_000).toISOString(),
      }),
  });
  const m = metrics.data;
  if (metrics.isLoading || !m) return <Spinner />;
  const revenueByDay = new Map<string, number>();
  for (const r of m.revenue)
    revenueByDay.set(r.date, (revenueByDay.get(r.date) ?? 0) + r.amount);
  const revenueByProduct = new Map<string, number>();
  for (const r of m.revenue)
    revenueByProduct.set(
      r.product,
      (revenueByProduct.get(r.product) ?? 0) + r.amount,
    );
  const currency = m.revenue[0]?.currency ?? "USD";
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Overview</h1>
        <div className="flex gap-1">
          {[7, 30, 90].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setDays(option)}
              className={`rounded-md px-2 py-1 text-xs ${
                option === days
                  ? "bg-brand-tint/50 font-medium text-brand-dark"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {option} days
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-5">
        <Stat label="Failed payments" value={m.failed_payments} />
        <Stat label="Open disputes" value={m.open_disputes} />
        <Stat label="Pending outbox" value={m.pending_outbox} />
        <Stat label="Dead outbox" value={m.dead_outbox} />
        <Stat label="Suspicious logins" value={m.suspicious_logins} />
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Bars
          title="New to this product"
          points={m.signups.map((p) => ({ date: p.date, value: p.count }))}
        />
        <Bars
          title="Active users"
          points={m.active_users.map((p) => ({ date: p.date, value: p.count }))}
        />
        <Bars
          title="Revenue"
          points={[...revenueByDay.entries()].map(([date, value]) => ({
            date,
            value: value / 100,
          }))}
        />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Card>
          <h2 className="text-sm font-medium">Revenue by product</h2>
          <ul className="mt-2 text-sm">
            {revenueByProduct.size === 0 ? <li>No revenue in range.</li> : null}
            {[...revenueByProduct.entries()].map(([product, amount]) => (
              <li key={product}>
                {product}: {formatMoney(amount, currency)}
              </li>
            ))}
          </ul>
        </Card>
        <Card>
          <h2 className="text-sm font-medium">Top balances</h2>
          <ul className="mt-2 text-sm">
            {m.top_balances.map((b) => (
              <li key={`${b.org_id}-${b.product}`}>
                <Link
                  className="text-brand underline"
                  to={
                    product
                      ? `/admin/p/${product}/orgs/${b.org_id}`
                      : `/admin/platform/orgs/${b.org_id}`
                  }
                >
                  {b.org_name}
                </Link>{" "}
                · {b.product} · {formatCredits(b.balance)}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <p className="text-xs text-slate-500">
        Dead deliveries and disputes:{" "}
        <Link className="text-brand underline" to="/admin/events?state=dead">
          events
        </Link>{" "}
        ·{" "}
        <Link
          className="text-brand underline"
          to="/admin/payments?state=disputed"
        >
          payments
        </Link>
      </p>
    </div>
  );
}
